import React, { useCallback } from "react";
import { ShieldCheck, Radar, Crosshair, Layers, Gavel, BrainCircuit } from "lucide-react";

const FEATURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/ed7eee19-61aa-456b-86f7-01a2255b2ba0/images/010920c32c624dc48974a52d72301d8c625f45cb68dcd0431c94fb7afd6b51dd.png";

const SERVICES = [
  {
    id: "audit",
    code: "S/01",
    title: "Algorithmic Risk Audit",
    icon: ShieldCheck,
    span: "lg:col-span-2 lg:row-span-2",
    blurb:
      "A forensic mapping of every algorithm currently rating, ranking, or routing your business — search, social, ads, credit, marketplaces. We name the exposure and quantify it.",
    bullets: ["Surface visibility audit", "Suppression detection", "Score & rank reconstruction"],
    featured: true,
  },
  {
    id: "intel",
    code: "S/02",
    title: "Threat Intelligence",
    icon: Radar,
    span: "lg:col-span-1",
    blurb: "Continuous monitoring of platform algorithm changes that move your revenue.",
    bullets: [],
  },
  {
    id: "counter",
    code: "S/03",
    title: "Counter-Algorithm Strategy",
    icon: Crosshair,
    span: "lg:col-span-1",
    blurb: "Offensive playbooks engineered to outrank, outdistribute, and outprice corrupt systems.",
    bullets: [],
  },
  {
    id: "defense",
    code: "S/04",
    title: "Business Defense Program",
    icon: Layers,
    span: "lg:col-span-1",
    blurb: "Retained program — quarterly audits, incident response, and platform diplomacy.",
    bullets: [],
  },
  {
    id: "policy",
    code: "S/05",
    title: "Exposure & Policy Briefs",
    icon: Gavel,
    span: "lg:col-span-1",
    blurb: "Publishable reports for regulators, press, and your legal team.",
    bullets: [],
  },
  {
    id: "ai",
    code: "S/06",
    title: "AI Risk Modeling",
    icon: BrainCircuit,
    span: "lg:col-span-2",
    blurb:
      "Simulate how new LLM-driven ranking, scoring, or moderation systems will treat your business — before they go live.",
    bullets: [],
  },
];

export default function Services() {
  const onMove = useCallback((e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative border-b border-white/10 py-24 md:py-32 bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-red-400 mb-6">
              /01 — Services
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight leading-[0.95]">
              Six instruments
              <br />
              for an <span className="text-red-500">untouched</span> industry.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed">
              Algorithmic risk is now the single largest unaudited liability on most balance
              sheets. Our work is split into six precise instruments — deployed independently or
              as a retained program.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                data-testid={`service-${s.id}`}
                onMouseMove={onMove}
                className={`svc-card relative bg-[#0a0a0a] p-8 md:p-10 ${s.span || ""} group`}
              >
                {s.featured && (
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                      backgroundImage: `url(${FEATURE_IMG})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                )}
                <div className="relative flex items-start justify-between mb-8">
                  <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-red-400">
                    {s.code}
                  </div>
                  <Icon
                    size={s.featured ? 38 : 22}
                    className="text-white/80 group-hover:text-red-500 transition-colors"
                  />
                </div>
                <h3
                  className={`relative font-heading font-bold uppercase tracking-tight ${
                    s.featured ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
                  }`}
                >
                  {s.title}
                </h3>
                <p className="relative mt-5 font-body text-sm md:text-base text-gray-300 leading-relaxed max-w-md">
                  {s.blurb}
                </p>
                {s.bullets.length > 0 && (
                  <ul className="relative mt-6 space-y-2">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400 flex items-center gap-3"
                      >
                        <span className="inline-block h-px w-4 bg-red-500" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                <a
                  href="#contact"
                  data-testid={`service-cta-${s.id}`}
                  className="relative mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white link-underline"
                >
                  Engage <span aria-hidden>→</span>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
